/**
 * BudgetAllocationPanel — the forward read (USAGE-AND-BUDGET.md §5/§7b).
 *
 * One reusable allocation editor for every level: org→tenants, tenant→networks,
 * network→rooms. Each child row shows its label + mode (%/abs) + value, the
 * resolved allocated points, and the running remaining headroom. Save validates
 * conservation client-side (Σ children ≤ parent) for instant feedback and lets
 * the server re-check on PUT (409 → conservation error surfaced inline).
 *
 * Points are the unit (1 pt = $0.001). ``childNoun`` labels the column
 * ("tenant" | "network" | "room").
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBudgetAllocation, putBudgetAllocation } from '@/services/budgetApi';

function fmtPts(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString()} pts`;
}

/** Resolve one child's points from its draft entry against the parent budget. */
function resolvePoints(entry, parentBudget) {
  if (!entry || parentBudget <= 0) return 0;
  const v = Number(entry.value) || 0;
  if (entry.mode === 'pct') return Math.max(0, Math.round((parentBudget * v) / 100));
  return Math.max(0, Math.min(Math.round(v), parentBudget));
}

export default function BudgetAllocationPanel({ scope, id, childNoun = 'child', title }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  /** Draft per-child allocation map: { childId: {mode, value} }. */
  const [draft, setDraft] = useState({});
  const [savedMsg, setSavedMsg] = useState(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setSavedMsg(null);
    try {
      const body = await getBudgetAllocation(scope, id);
      setData(body);
      const d = {};
      for (const c of body.children || []) {
        if (c.mode && c.value != null) d[c.id] = { mode: c.mode, value: Number(c.value) };
      }
      setDraft(d);
    } catch (e) {
      setError(e?.message || 'Failed to load allocations');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [scope, id]);

  useEffect(() => { load(); }, [load]);

  const parentBudget = Number(data?.parentBudgetPoints) || 0;
  const unlimited = !!data?.unlimited;

  const allocatedTotal = useMemo(
    () => Object.values(draft).reduce((s, e) => s + resolvePoints(e, parentBudget), 0),
    [draft, parentBudget],
  );
  const remaining = unlimited ? Infinity : Math.max(0, parentBudget - allocatedTotal);
  const overCommitted = !unlimited && allocatedTotal > parentBudget;

  const setChild = (cid, patch) =>
    setDraft((prev) => ({ ...prev, [cid]: { mode: 'pct', value: 0, ...prev[cid], ...patch } }));
  const clearChild = (cid) =>
    setDraft((prev) => { const n = { ...prev }; delete n[cid]; return n; });

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSavedMsg(null);
    try {
      const allocations = {};
      for (const [cid, e] of Object.entries(draft)) {
        if (e && Number(e.value) > 0) allocations[cid] = { mode: e.mode, value: Number(e.value) };
      }
      const body = await putBudgetAllocation(scope, id, allocations);
      setData(body);
      setSavedMsg('Saved.');
    } catch (e) {
      if (e?.status === 409 && e?.data) {
        setError(
          `Allocations exceed the budget: ${fmtPts(e.data.allocatedTotalPoints)} of ${fmtPts(e.data.parentBudgetPoints)}.`,
        );
      } else {
        setError(e?.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  }, [draft, scope, id]);

  if (loading && !data) {
    return <div className="py-10 text-center text-sm text-gray-500">Loading allocations…</div>;
  }

  return (
    <div className="space-y-4" data-testid="budget-allocation-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title || `Budget by ${childNoun}`}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {unlimited
              ? 'This budget is unlimited (off-platform / Enterprise) — allocations are advisory.'
              : <>Parent budget <strong className="text-gray-700">{fmtPts(parentBudget)}</strong> · allocated{' '}
                  <strong className={overCommitted ? 'text-red-600' : 'text-gray-700'}>{fmtPts(allocatedTotal)}</strong>{' '}
                  · remaining <strong className="text-emerald-700">{remaining === Infinity ? '∞' : fmtPts(remaining)}</strong></>}
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || overCommitted}
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save allocations'}
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      ) : null}
      {savedMsg ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{savedMsg}</div>
      ) : null}
      {overCommitted ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Allocations exceed the parent budget — reduce before saving (conservation).
        </div>
      ) : null}

      {(data?.children || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-10 text-center text-sm text-gray-500">
          No {childNoun}s to allocate to yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-100">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 capitalize">{childNoun}</th>
                <th className="px-3 py-2">Mode</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-right">Allocated</th>
                <th className="px-3 py-2 text-right">Consumed</th>
              </tr>
            </thead>
            <tbody>
              {(data?.children || []).map((c) => {
                const e = draft[c.id];
                const pts = resolvePoints(e, parentBudget);
                return (
                  <tr key={c.id} className="border-b border-gray-50" data-testid={`alloc-row-${c.id}`}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      <span className="block">{c.label || c.id}</span>
                      <code className="text-[11px] text-gray-400">{c.id}</code>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={e?.mode || 'pct'}
                        onChange={(ev) => setChild(c.id, { mode: ev.target.value })}
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                      >
                        <option value="pct">% of parent</option>
                        <option value="abs">Absolute pts</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        value={e?.value ?? ''}
                        onChange={(ev) => {
                          const raw = ev.target.value;
                          if (raw === '') return clearChild(c.id);
                          setChild(c.id, { value: Number(raw) });
                        }}
                        className="w-28 rounded border border-gray-300 px-2 py-1 text-xs text-right tabular-nums"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                      {fmtPts(pts)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {c.consumedPoints != null ? fmtPts(c.consumedPoints) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
